import { useState, useEffect, forwardRef } from 'react'
import EditableInput from './EditableInput'
import s from './styles.scss'
import cn from 'classnames'
import TextareaAutosize from 'react-textarea-autosize'
import { Button } from 'reactstrap'
import { NumericFormat } from 'react-number-format'
import { getCurrency } from 'utils/authService'

const initialInvoice = {
  productLineDescription: 'Item Description',
  productLineQuantity: 'Qty',
  productLineQuantityRate: 'Rate',
  productLineQuantityAmount: 'Amount',
  productLines: [
    {
      description: '',
      quantity: '',
      rate: '0.00'
    }
  ],
  subTotalLabel: 'Subtotal',
  taxLabel: 'Sale Tax (10%)',
  totalLabel: 'TOTAL',
  currency: getCurrency().symbol
}

const PricingTable = forwardRef((props, ref) => {
  const { isPdf, content, actions, icons, isStandAloneComponent } = props
  const [invoice, setInvoice] = useState(initialInvoice)
  const [subTotal, setSubTotal] = useState()
  const [saleTax, setSaleTax] = useState()

  useEffect(() => {
    if (content) {
      setInvoice(JSON.parse(content))
    }
  }, [content])

  const handleChange = (name, value) => {
    if (name !== 'productLines') {
      const newInvoice = { ...invoice }
      newInvoice[name] = value

      setInvoice(newInvoice)
    }
  }

  const handleProductLineChange = (index, name, value) => {
    const productLines = invoice.productLines.map((productLine, i) => {
      if (i === index) {
        const newProductLine = { ...productLine }

        if (name === 'description') {
          newProductLine[name] = value
        } else {
          if (
            value[value.length - 1] === '.' ||
            (value[value.length - 1] === '0' && value.includes('.'))
          ) {
            newProductLine[name] = value
          } else {
            const n = parseFloat(value)

            newProductLine[name] = (n ? n : 0).toString()
          }
        }

        return newProductLine
      }

      return { ...productLine }
    })

    setInvoice({ ...invoice, productLines })
  }

  const handleRemove = i => {
    const productLines = invoice.productLines.filter((productLine, index) => index !== i)

    setInvoice({ ...invoice, productLines })
  }

  const handleAdd = () => {
    const productLines = [
      ...invoice.productLines,
      {
        description: '',
        quantity: '1',
        rate: '0.00'
      }
    ]

    setInvoice({ ...invoice, productLines })
  }

  const calculateAmount = (quantity, rate) => {
    const quantityNumber = parseFloat(quantity)
    const rateNumber = parseFloat(rate)
    const amount = quantityNumber && rateNumber ? quantityNumber * rateNumber : 0

    return amount.toFixed(2)
  }

  useEffect(() => {
    let subTotal = 0

    invoice.productLines.forEach(productLine => {
      const quantityNumber = parseFloat(productLine.quantity)
      const rateNumber = parseFloat(productLine.rate)
      const amount = quantityNumber && rateNumber ? quantityNumber * rateNumber : 0

      subTotal += amount
    })

    setSubTotal(subTotal)
  }, [invoice.productLines])

  useEffect(() => {
    const match = invoice.taxLabel.match(/[-]{0,1}[\d]*[.]{0,1}[\d]+/g)
    const taxRate = match ? parseFloat(match[0]) : 0
    const saleTax = subTotal ? (subTotal * taxRate) / 100 : 0

    setSaleTax(saleTax)
  }, [subTotal, invoice.taxLabel])

  return (
    <>
      {icons}
      <div
        ref={ref}
        className={s.invoiceWrapper}
        style={{
          position: 'relative',
          background: '#fff',
          fontFamily: 'Arial, sans-serif',
          fontSize: '11pt',
          ...(!isPdf ? { padding: '0 20px 0 0' } : {})
        }}
      >
        <div
          style={{
            marginTop: '30px',
            backgroundColor: '#666',
            display: 'flex'
          }}
        >
          <div
            style={{
              width: '48%',
              padding: '4px 8px'
            }}
          >
            <EditableInput
              style={{
                color: '#fff',
                fontWeight: '600'
              }}
              className={cn(s.input, s.white, s.bold)}
              value={invoice.productLineDescription}
              onChange={value => handleChange('productLineDescription', value)}
              isPdf={isPdf}
            />
          </div>
          <div
            style={{
              width: '17%',
              padding: '4px 8px'
            }}
          >
            <EditableInput
              style={{
                color: '#fff',
                fontWeight: '600',
                textAlign: 'right'
              }}
              className={cn(s.input, s.white, s.bold, s.right)}
              value={invoice.productLineQuantity}
              onChange={value => handleChange('productLineQuantity', value)}
              isPdf={isPdf}
            />
          </div>
          <div
            style={{
              width: '17%',
              padding: '4px 8px'
            }}
          >
            <EditableInput
              style={{
                color: '#fff',
                fontWeight: '600',
                textAlign: 'right'
              }}
              className={cn(s.input, s.white, s.bold, s.right)}
              value={invoice.productLineQuantityRate}
              onChange={value => handleChange('productLineQuantityRate', value)}
              isPdf={isPdf}
            />
          </div>
          <div
            style={{
              width: '18%',
              padding: '4px 8px',
              textAlign: 'right'
            }}
          >
            <EditableInput
              style={{
                color: '#fff',
                fontWeight: '600',
                textAlign: 'right'
              }}
              className={cn(s.input, s.white, s.bold, s.right)}
              value={invoice.productLineQuantityAmount}
              onChange={value => handleChange('productLineQuantityAmount', value)}
              isPdf={isPdf}
            />
          </div>
        </div>

        {invoice.productLines.map((productLine, i) => {
          return (
            <div
              key={i}
              className={s.row}
              style={{
                display: 'flex',
                minHeight: '38px',
                justifyContent: 'space-between',
                position: 'relative',
                borderBottom: '1px solid #e3e3e3'
              }}
            >
              <div
                style={{
                  width: '48%',
                  padding: '4px 8px',
                  paddingBottom: '10px'
                }}
              >
                {isPdf ? (
                  productLine.description ? (
                    <span
                      style={{
                        display: 'inline-block',
                        width: '98%',
                        padding: '4px 12px 4px 0',
                        marginBottom: '1px',
                        border: '1px dotted transparent',
                        transition: 'background 0.1s ease-in-out, border-color 0.2s ease-in-out',
                        color: '#222'
                      }}
                    >
                      {productLine.description}
                    </span>
                  ) : (
                    ''
                  )
                ) : (
                  <TextareaAutosize
                    className={cn(s.input, s.dark)}
                    minRows={1}
                    placeholder="Enter item name/description"
                    value={productLine.description || ''}
                    onChange={({ target }) =>
                      handleProductLineChange(i, 'description', target.value)
                    }
                  />
                )}
              </div>
              <div
                style={{
                  width: '17%',
                  padding: '4px 8px',
                  paddingBottom: '10px'
                }}
              >
                <EditableInput
                  style={{
                    color: '#222',
                    textAlign: 'right'
                  }}
                  className={cn(s.input, s.dark, s.right)}
                  value={productLine.quantity}
                  onChange={value => handleProductLineChange(i, 'quantity', value)}
                  isPdf={isPdf}
                />
              </div>
              <div
                style={{
                  width: '17%',
                  padding: '4px 8px',
                  paddingBottom: '10px'
                }}
              >
                <NumericFormat
                  style={{
                    display: 'inline-block',
                    width: '98%',
                    padding: '4px 12px 4px 0',
                    marginBottom: '1px',
                    border: '1px dotted transparent',
                    transition: 'background 0.1s ease-in-out, border-color 0.2s ease-in-out',
                    textAlign: 'right',
                    color: '#222'
                  }}
                  className={isPdf ? '' : cn(s.input, s.dark, s.right)}
                  thousandSeparator
                  decimalScale={2}
                  value={productLine.rate}
                  onChange={formattedValue => {
                    const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                    handleProductLineChange(i, 'rate', stripValue)
                  }}
                  prefix={getCurrency().symbol}
                  displayType={isPdf ? 'text' : 'input'}
                />
              </div>
              <div
                style={{
                  width: '18%',
                  padding: '4px 8px',
                  paddingBottom: '10px'
                }}
              >
                <NumericFormat
                  style={{
                    display: 'inline-block',
                    width: '98%',
                    padding: '4px 12px 4px 0',
                    marginBottom: '1px',
                    border: '1px dotted transparent',
                    transition: 'background 0.1s ease-in-out, border-color 0.2s ease-in-out',
                    textAlign: 'right',
                    color: '#222'
                  }}
                  thousandSeparator
                  decimalScale={2}
                  value={calculateAmount(productLine.quantity, productLine.rate)}
                  prefix={getCurrency().symbol}
                  displayType="text"
                />
              </div>
              {!isPdf && (
                <button
                  className={cn(s.link, s.row__remove)}
                  aria-label="Remove Row"
                  title="Remove Row"
                  onClick={() => handleRemove(i)}
                >
                  <span className={cn(s.icon, s['icon-remove'], s['bg-red'])}></span>
                </button>
              )}
            </div>
          )
        })}

        <div
          style={{
            display: 'flex'
          }}
        >
          <div
            style={{
              width: '50%',
              marginTop: '10px'
            }}
          >
            {!isPdf && (
              <button className={s.link} onClick={handleAdd}>
                <span className={cn(s.icon, s['icon-add'], s['bg-green'], s['mr-10'])}></span>
                Add Line Item
              </button>
            )}
          </div>
          <div
            style={{
              width: '50%',
              marginTop: '20px'
            }}
          >
            <div
              style={{
                display: 'flex'
              }}
            >
              <div
                style={{
                  width: '50%',
                  padding: '5px'
                }}
              >
                <EditableInput
                  className={s.input}
                  value={invoice.subTotalLabel}
                  onChange={value => handleChange('subTotalLabel', value)}
                  isPdf={isPdf}
                />
              </div>
              <div
                style={{
                  width: '50%',
                  padding: '5px'
                }}
              >
                <NumericFormat
                  style={{
                    display: 'inline-block',
                    width: '98%',
                    padding: '4px 12px 4px 0',
                    marginBottom: '1px',
                    border: '1px dotted transparent',
                    transition: 'background 0.1s ease-in-out, border-color 0.2s ease-in-out',
                    textAlign: 'right',
                    color: '#222',
                    fontWeight: '600'
                  }}
                  thousandSeparator
                  decimalScale={2}
                  value={subTotal?.toFixed(2)}
                  prefix={getCurrency().symbol}
                  displayType="text"
                />
              </div>
            </div>
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  width: '50%',
                  padding: '5px'
                }}
              >
                <EditableInput
                  className={s.input}
                  value={invoice.taxLabel}
                  onChange={value => handleChange('taxLabel', value)}
                  isPdf={isPdf}
                />
              </div>
              <div
                style={{
                  width: '50%',
                  padding: '5px'
                }}
              >
                <NumericFormat
                  style={{
                    display: 'inline-block',
                    width: '98%',
                    padding: '4px 12px 4px 0',
                    marginBottom: '1px',
                    border: '1px dotted transparent',
                    transition: 'background 0.1s ease-in-out, border-color 0.2s ease-in-out',
                    textAlign: 'right',
                    color: '#222',
                    fontWeight: '600'
                  }}
                  thousandSeparator
                  decimalScale={2}
                  value={saleTax?.toFixed(2)}
                  prefix={getCurrency().symbol}
                  displayType="text"
                />
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                padding: '5px',
                backgroundColor: '#e3e3e3'
              }}
            >
              <div
                style={{
                  width: '50%',
                  padding: '5px'
                }}
              >
                <EditableInput
                  style={{
                    fontWeight: '600'
                  }}
                  className={cn(s.input, s.bold)}
                  value={invoice.totalLabel}
                  onChange={value => handleChange('totalLabel', value)}
                  isPdf={isPdf}
                />
              </div>
              <div
                style={{
                  width: '50%',
                  padding: '5px',
                  ...(isPdf ? { textAlign: 'right' } : { display: 'flex' })
                }}
              >
                <EditableInput
                  style={{
                    color: '#222',
                    textAlign: 'right',
                    fontWeight: '600',
                    marginLeft: '30px',
                    visibility: 'hidden'
                  }}
                  className={cn(s.input, s.dark, s.bold, s.right, s['ml-30'])}
                  value={invoice.currency}
                  onChange={value => handleChange('currency', value)}
                  isPdf={isPdf}
                  hidden
                />
                <NumericFormat
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px 4px 0',
                    marginBottom: '1px',
                    border: '1px dotted transparent',
                    transition: 'background 0.1s ease-in-out, border-color 0.2s ease-in-out',
                    textAlign: 'right',
                    color: '#222',
                    fontWeight: '600',
                    width: 'auto !important'
                  }}
                  thousandSeparator
                  decimalScale={2}
                  value={(typeof subTotal !== 'undefined' && typeof saleTax !== 'undefined'
                    ? subTotal + saleTax
                    : 0
                  ).toFixed(2)}
                  prefix={getCurrency().symbol}
                  displayType="text"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {actions && (
        <div className={s.actionBtnsWrapper}>
          {actions.map((button, index) => (
            <Button
              key={index}
              className={cn({
                [button.className]: button.className
              })}
              onClick={() => {
                button.cb(!isStandAloneComponent ? JSON.stringify(invoice) : invoice)
              }}
              disabled={button.enabled ? !button.enabled : false}
            >
              {button.text}
            </Button>
          ))}
        </div>
      )}
    </>
  )
})

export default PricingTable
